import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { loadVGSCollect } from "@vgs/collect-js";
import { BehaviorSubject, from, take } from "rxjs";
import { AppSettingsService } from "src/app/services/app-settings.service";
import { UnitCardPinAction, UnitCardPinParams } from "./UnitCardPinContext";

interface VGSFormInterface {
  field: (data: string, obj: {}) => {};
  submit: (
    data: string,
    obj: {},
    func: (status, response) => void
  ) => VGSFormInterface;
  SERIALIZERS: any;
}
interface VGSCollectInterface {
  create: (func: (state) => void) => VGSFormInterface;
  init: (func: (state) => void) => VGSFormInterface;
}

@Component({
  selector: "terra-unit-card-pin-dialog",
  templateUrl: "./unit-card-pin-dialog.component.html",
  styleUrls: ["./unit-card-pin-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitCardPinDialogComponent implements OnInit {
  UnitCardPinAction = UnitCardPinAction;
  Object = Object;
  form: any = null;
  forgotPin: boolean = false;
  dialogTitle: string;
  responseError$ = new BehaviorSubject<string>(null);
  state$ = new BehaviorSubject<any>(null);
  showAllErrors = false;
  textStyle = {
    color: "#002138 !important",
    "font-family": "Roboto",
    "font-size": "16px",
    "font-weight": "400",
    "line-height": "22px",
  };
  styles = {
    ...this.textStyle,
    background: "#FFFFFF",
    border: "1px solid #E0E0E0",
    boxSizing: "border-box",
    height: "50px",
    padding: "14px 8px",
    ["&:focus"]: { border: "2px solid #3D41EB" },
    ["&.invalid.touched:not(:focus)"]: { border: "2px solid #F91919" },
    ["&::placeholder"]: { ...this.textStyle, color: "#A0A0A0 !important" },
  };
  fields = {
    [UnitCardPinAction.SET_PIN]: [
      {
        label: "Enter 4-digit PIN Code",
        class: "cc-pin",
        labelContainerClass: "cc-pin-container",
        vcsFieldDefinition: {
          name: "#cc-pin",
          definition: {
            type: "text",
            name: "data.attributes.pin",
            successColor: "#4F8A10",
            errorColor: "#D8000C",
            placeholder: "1234",
            maxLength: 4,
            validations: ["required", "/^([0-9]{4,6})$/"],
            css: this.styles,
          },
        },
      },
      {
        label: "Confirm PIN Code",
        class: "cc-pin-confirm",
        labelContainerClass: "cc-pin-confirm-container",
        vcsFieldDefinition: {
          name: "#cc-pin-confirm",
          definition: {
            type: "text",
            name: "data.attributes.pin-confirm",
            successColor: "#4F8A10",
            errorColor: "#D8000C",
            placeholder: "1234",
            maxLength: 4,
            validations: [
              "required",
              "/^([0-9]{4})$/",
              {
                type: "compareValue",
                params: {
                  field: "data.attributes.pin",
                  function: "match",
                },
              },
            ],
            css: this.styles,
          },
        },
      },
    ],
    [UnitCardPinAction.RESET_PIN]: [
      {
        label: "Old PIN Code",
        class: "cc-old-pin",
        labelContainerClass: "old-pin-label-container",
        vcsFieldDefinition: {
          name: "#cc-old-pin",
          definition: {
            type: "text",
            name: "data.attributes.oldPin",
            successColor: "#4F8A10",
            errorColor: "#D8000C",
            placeholder: "1234",
            maxLength: 4,
            validations: ["required", "/^([0-9]{4})$/"],
            css: this.styles,
          },
        },
      },
      {
        label: "New PIN Code",
        class: "cc-new-pin",
        labelContainerClass: "cc-new-pin-container",
        vcsFieldDefinition: {
          name: "#cc-new-pin",
          definition: {
            type: "text",
            name: "data.attributes.newPin",
            successColor: "#4F8A10",
            errorColor: "#D8000C",
            placeholder: "1234",
            maxLength: 4,
            validations: ["required", "/^([0-9]{4})$/"],
            css: this.styles,
          },
        },
      },
      {
        label: "Confirm New PIN Code",
        class: "cc-new-pin-confirm",
        labelContainerClass: "cc-new-pin-confirm-container",
        vcsFieldDefinition: {
          name: "#cc-new-pin-confirm",
          definition: {
            type: "text",
            name: "data.attributes.newPin-confirm",
            successColor: "#4F8A10",
            errorColor: "#D8000C",
            placeholder: "1234",
            maxLength: 4,
            validations: [
              "required",
              "/^([0-9]{4})$/",
              {
                type: "compareValue",
                params: {
                  field: "data.attributes.newPin",
                  function: "match",
                },
              },
            ],
            css: this.styles,
          },
        },
      },
    ],
  };
  reqOpt = {
    method:
      this.data.unitCardPinAction === UnitCardPinAction.SET_PIN
        ? "POST"
        : "PUT",
    // This converts the dot-separated field name string into a JSON object
    mapDotToObject: "true",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Authorization: `Bearer ${this.data.unitCustomerToken}`,
    },
  };
  errors = {
    "comparison failed": "PIN Code doesn't match. Try again",
    "is required": "Required",
    "the value doesn't match a pattern": "PIN Code should be 4 digits",
    "Incorrect or invalid PIN.": "Incorrect Old PIN.",
  };
  loadVGSCollect$ = from(
    loadVGSCollect({
      vaultId: this.appSettingService.VGSVault, // required
      environment: this.appSettingService.VGSEnvironment,
      version: "2.18.3",
    })
  );

  loading$ = new BehaviorSubject<boolean>(true);

  constructor(
    private dialogRef: MatDialogRef<UnitCardPinDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UnitCardPinParams,
    private appSettingService: AppSettingsService
  ) {}

  ngOnInit(): void {
    this.dialogTitle =
      this.data.unitCardPinAction === UnitCardPinAction.SET_PIN
        ? "Set a PIN Code"
        : "Reset a PIN Code";
    this.loadVGSCollect$.pipe(take(1)).subscribe({
      next: (collect: VGSCollectInterface) => {
        this.form = collect.init((state) => {
          this.state$.next(state);
        });
        this.fields?.[this.data.unitCardPinAction]?.forEach((actionField) => {
          const definition = actionField.vcsFieldDefinition;
          this.form.field(definition.name, definition.definition);
        });
        this.loading$.next(false);
      },
      error: (e) => {},
    });
  }

  submitPin() {
    if (!this.form) {
      return;
    }
    if (this.form && this.isFormErrorState()) {
      this.showAllErrors = true;
      return;
    }
    this.sendToUnit();
  }

  isFormErrorState() {
    const state = this.state$.getValue();
    const errors = Object.keys(state)
      .map((key) => state[key].errors)
      .reduce((acc, val) => [...acc, ...val], []);
    return !!errors.length;
  }

  sendToUnit() {
    this.form.submit(
      `/cards/${this.data.cardId}/secure-data/pin`,
      this.reqOpt,
      (status, response) => {
        if (status == 200) {
          this.data.businessVirtualDebitCard.isPinCodeExist = true;
          this.dialogRef.close({
            isOk: true,
            card: this.data.businessVirtualDebitCard,
          });
        }
        this.responseError$.next(response.errors[0].title);
      },
      (error) => {
        console.error(error);
        this.dialogRef.close({ isOk: false });
      }
    );
  }
}
